import {useTranslation} from "react-i18next";
import type {MarketingHealthV4} from "../../models/marketing-v4";
import type {StatCardData} from "../../kit/StatCard";
import {StatGrid} from "../../kit/StatGrid";
import type {InsightsLoading} from "../../kit/insightsLoading";

interface MarketingHealthStatCardsProps {
    health?: MarketingHealthV4;
    loading?: InsightsLoading;
}

function formatNumber(value: number | null | undefined, locale: string): string {
    return value == null ? "—" : value.toLocaleString(locale, {maximumFractionDigits: 0});
}

function formatPercentage(value: number | null, locale: string): string {
    return value == null
        ? "—"
        : `${value.toLocaleString(locale, {maximumFractionDigits: 1})}%`;
}

export function MarketingHealthStatCards({
    health,
    loading,
}: MarketingHealthStatCardsProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const hardBounceRate = health && health.totalContacts > 0
        ? (health.hardBouncedContacts / health.totalContacts) * 100
        : null;

    const items: StatCardData[] = [
        {
            id: "marketable",
            title: t("Marketable contacts"),
            value: formatNumber(health?.marketableContacts, locale),
            hint: t("Contacts matching your marketing-consent rules and carrying a usable email address."),
        },
        {
            id: "newsletterOptIns",
            title: t("Newsletter opt-ins"),
            value: formatNumber(health?.newsletterOptIns, locale),
            hint: t("Contacts subscribed to newsletters. Newsletter consent is independent of general email consent."),
        },
        {
            id: "hardBounceRate",
            title: t("Hard-bounce rate"),
            value: formatPercentage(hardBounceRate, locale),
            hint: health == null
                ? t("Share of contacts marked as permanently undeliverable.")
                : t("Share of contacts marked as permanently undeliverable: {{formattedCount}} contacts.", {
                    formattedCount: health.hardBouncedContacts.toLocaleString(locale, {maximumFractionDigits: 0}),
                }),
        },
        {
            id: "openRate",
            title: t("Tracked open rate"),
            value: formatPercentage(health?.trackedOpenRate ?? null, locale),
            hint: t("Opened recipient records divided by all recipient records synchronized from the newsletter provider. This is all-time, not date-filtered."),
        },
        {
            id: "trackedSends",
            title: t("Tracked sends"),
            value: formatNumber(health?.trackedSends, locale),
            hint: t("Recipient-level mailing records synchronized from the newsletter provider. This is the all-time sample size behind the open rate."),
        },
        {
            id: "campaigns",
            title: t("Synced campaigns"),
            value: formatNumber(health?.syncedCampaigns, locale),
            hint: t("Distinct synchronized mailing IDs. Use this with tracked sends to judge how representative the open rate is."),
        },
    ];

    return <StatGrid items={items} loading={loading}/>;
}

export default MarketingHealthStatCards;
